/** getVariants
 *
 * Calculates the available variants based on the selected material
 * @param variants Array of variant data grouped by variant code
 */
export const getVariants = (variants) => {
  if (!variants?.length) return [];
  /** Array containing material id, code and variant values
   *  @returns {
   *  [
   *    {
   *      materialId: "20MU000RUS"
   *      code: "A11969"
   *      value: 8
   *    },
   *    {
   *      materialId: "20MU000PUS"
   *      code: "processor_type_variant"
   *      value: "ryzen_5_pro"
   *    }
   *  ]
   * }
   */
  let productTable = [];

  const productScores = new Map();
  /** Map table of available product variants
   * @returns {
   *   Map {
   *     '1N4-00001' => Map {
   *       'A00040' => {
   *         code: 'A00040',
   *         label: 'Processor / Clock Speed',
   *         value: 1.7,
   *         unit: {
   *           code: 'GIGAHERTZ',
   *           label: 'Gigahertz',
   *           symbol: 'GHz'
   *         },
   *         options: =>  Map {
   *           'default' {
   *             value: 1.7,
   *             unit: {
   *               code: 'GIGAHERTZ',
   *               label: 'Gigahertz',
   *               symbol: 'GHz'
   *             },
   *             measurement: true,
   *             selected: true,
   *             selectable: false
   *            }
   *           'SMW-00001' {
   *              materialId: "SMW-00001"
   *              description: "Microsoft Surface Book 3 - 15" - Core i7 1065G7 - 32 GB RAM - 1 TB SSD - English"
   *              manufacturerId: "0007000444"
   *              manufacturerName: "MICROSOFT"
   *              manufacturerPartNumber: "SMW-00001"
   *              value: 32
   *              unit: {
   *                code: 'GIGAHERTZ',
   *                label: 'Gigahertz',
   *                symbol: 'GHz'
   *              },
   *              measurement: true
   *          }
   *       }
   *     }
   *   }
   * }
   * */
  const variantTable = variants.reduce((variants, variant) => {
    // Reduce variant products to variant options
    const variantProducts = variant.variantProducts.reduce(
      (products, product) => {
        // Set value based on if theres a option or a measurement
        const value =
          product.measurementValue?.amount || product.optionValue?.code;
        // Set unit to either the measurement unit or variant options
        const unit = product.measurementValue
          ? product.measurementValue.unit
          : product.optionValue;
        // Check if the current product is in product scores
        if (productScores.has(product.materialId)) {
          let updatedScore = productScores.get(product.materialId);
          productScores.set(product.materialId, (updatedScore += 1));
        } else productScores.set(product.materialId, 1);
        // Add current product to product table
        productTable = [
          ...productTable,
          { materialId: product.materialId, code: variant.code, value },
        ];
        // Add product to product table
        return products.set(product.materialId, {
          materialId: product.materialId,
          description: product.description,
          manufacturerId: product.manufacturerId,
          manufacturerName: product.manufacturerName,
          manufacturerPartNumber: product.manufacturerPartNumber,
          value,
          unit,
          measurement: product.measurement,
        });
      },
      new Map()
    );
    // Set value based on if theres a option or a measurement
    const value = variant.measurementValue?.amount || variant.optionValue?.code;
    // Set unit to either the measurement unit or variant options
    const unit = variant.measurementValue
      ? variant.measurementValue.unit
      : variant.optionValue;
    // Check if variant contains variant products and has a default value
    if (!variantProducts.size && !value) return variants;
    // Add the default variant to variant list
    variantProducts.set('default', {
      value,
      unit,
      measurement: variant.measurement,
      selected: true,
      selectable: false,
    });
    // String compare using collator. Used instead of localeCompare for performance
    // ENHANCEMENT: internationalization
    const collator = new Intl.Collator(undefined, {
      // Set to handle numeric values
      numeric: true,
      // Set to compare the case and non numeric
      sensitivity: 'base',
    });
    // Sort variant options
    const sortedVariantProducts = [...variantProducts.entries()].sort(
      ([aKey, a2], [bKey, b2]) => {
        // Check if current index contains a hex value
        const isColor = /[0-9A-Fa-f]{6}/g.test(a2?.value);
        // Sort using label for hex values
        if (isColor) return collator.compare(a2?.unit?.label, b2?.unit?.label);
        return collator.compare(a2?.value, b2?.value);
      }
    );
    // Return updated variant map containing the variant options
    return variants.set(variant.code, {
      code: variant.code,
      label: variant.label,
      value,
      unit,
      measurement: variant.measurement,
      options: new Map(sortedVariantProducts),
    });
  }, new Map());

  // Sort product table by ascending score
  const sortedProductTable = productTable.sort((a, b) => {
    const aScore = productScores.get(a.materialId);
    const bScore = productScores.get(b.materialId);
    return aScore - bScore;
  });

  // Create a set to store checked variants
  const checked = new Set();
  // Filter and map sorted product table to remove checked variants
  sortedProductTable
    .filter(({ materialId, code, value }) => {
      // Check if the current material is selectable based on its score
      if (productScores.get(materialId) === 1 && variantTable.get(code)) {
        const foundOption = variantTable.get(code).options.get(materialId);
        if (foundOption) foundOption.selectable = true;
      }
      // Check if the current key is in the checked list
      const key = `${code}-${value}`;
      if (!checked.has(key)) {
        checked.add(key);
        return null;
      }
      return checked.has(key);
    })
    .map(({ materialId, code, value }) =>
      // remove check materials from the variant table
      variantTable.get(code).options.delete(materialId)
    );
  return Array.from(variantTable.values());
};
